import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Typography as MuiTypography } from '@mui/material';
import sanitizeHtml from 'sanitize-html';
import queryString from 'query-string';
import camelcaseKeys from 'camelcase-keys';

import ROUTES from '@src/constants/route';
import { PACKAGE_TYPE, RESOURCES_TYPE } from '@src/constants/package';
import { VOUCHER_TYPE } from '@src/constants/voucher';
import { EVENT_TYPE } from '@src/constants';
import { checkVietNam } from '@src/utils/checkCountry';
import {
  checkPackageDiscount,
  getCustomPackagePrice,
  generateSelectOptions,
  sortCustomResourceKeys,
  formatNumberWithSuffix,
  checkStudioCustomPackage,
} from '@src/services/package';
import {
  getOptimalVouchersForPackage,
  getPriceFromVoucher,
  getSavedPrice,
} from '@src/services/voucher';
import { getTotalPrice } from '@src/services/price';
import { PAYMENT_CURRENCY_UNIT } from '@src/constants/payment';
import CustomTypography from '@src/new-components/Typography';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import useIpLocation from '@src/hooks/useLocation';
import apis from '@src/apis';
import NewCustomOptions from '@src/components/NewCustomOptions';

import NotificationDialogV3 from '@src/components/NotificationDialogV3';
import actions from '@src/redux/actions';
import FeaturesDescriptionList from './FeaturesDescriptionList';
import NewPackagePrice from './components/PackagePrice';
import NewPackageHeader from '../components/PackageHeader';
import DetailsBox from '../components/DetailsBox';
import {
  StyledNewCustomPackageWrapper,
  StyledNewCustomPackageCard,
  StyledNewCustomPackageContent,
  StyledBuyButton,
  StyledOptionLabel,
  StyledCreditSelectorWrapper,
} from './index.style';

// === Utility ===
const formatPrice = ({ vndPrice, usdPrice, language, isVietNam }) => {
  if (isVietNam) {
    const unitPrice = "<span style='text-decoration: underline;'>đ</span>";
    return `${unitPrice}${vndPrice?.toLocaleString(language)}`;
  }
  return `$${usdPrice?.toLocaleString(language)}`;
};

// === Sub-component: BuyPackageButton ===
const BuyPackageButton = ({
  packageCode,
  voucherCode,
  credits,
  devices,
  voiceClonings,
  concurrentRequestApi,
  onBeforeBuy,
}) => {
  const { t } = useTranslation();
  const history = useHistory();

  const handleClick = () => {
    const params = new URLSearchParams();
    if (packageCode) params.set('package', packageCode);
    if (voucherCode) params.set('voucherCode', voucherCode);
    if (credits) params.set('credits', credits);
    if (devices != null && devices >= 0) params.set('devices', devices);
    if (voiceClonings != null && voiceClonings >= 0)
      params.set('voiceClonings', voiceClonings);
    if (concurrentRequestApi != null && concurrentRequestApi >= 0)
      params.set('concurrentRequestApi', concurrentRequestApi);

    const paymentUrl = `${ROUTES.PAYMENT}?${params.toString()}`;

    if (onBeforeBuy) onBeforeBuy(paymentUrl);

    history.push(paymentUrl);
  };

  return (
    <StyledBuyButton type="button" onClick={handleClick} data-id="add-to-cart">
      {t('buyNow')}
    </StyledBuyButton>
  );
};

// === Sub-component: CreditSelector ===
const CreditSelector = ({
  creditResource,
  selectedCredits,
  onCreditChange,
  language,
  handleClickShowPopup,
  pkg,
  bestVoucher,
  optionsSelected,
  apiTtsSttLaunchEnabled,
}) => {
  const { t } = useTranslation();

  if (!creditResource) return null;

  const { options, title, tooltip, description } = creditResource;

  const selectedOption =
    options?.find((opt) => opt.value === selectedCredits) || options?.[0];

  const headerOption = (
    <StyledOptionLabel>{title?.[language]}</StyledOptionLabel>
  );

  const renderTooltipOption = () => {
    if (tooltip && tooltip?.[language]) {
      return (
        <MuiTypography
          component="span"
          sx={{ fontSize: '14px' }}
          style={{ textAlign: 'left' }}
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(tooltip?.[language], {
              allowedTags: ['ul', 'li'],
              allowedAttributes: {
                li: ['style'],
                ul: ['style'],
              },
              transformTags: {
                li: (_, attribs) => ({
                  tagName: 'li',
                  attribs: {
                    ...attribs,
                    style: 'list-style-type: disc;',
                  },
                }),
              },
            }),
          }}
        />
      );
    }
    return null;
  };

  const isVietNam = checkVietNam();

  const infoOption = !!description && (
    <div
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{
        __html: sanitizeHtml(
          t(description[language], {
            pricePerUnit: formatPrice({
              vndPrice: selectedOption?.vndPricePerUnit,
              usdPrice: selectedOption?.usdPricePerUnit,
              language,
              isVietNam,
            }),
          }),
          {
            allowedTags: ['span'],
            allowedAttributes: { span: ['style'] },
          },
        ),
      }}
    />
  );

  const getMinValue = (key) => pkg?.customResource?.[key]?.min;
  const getLocalizedText = (minValue, value) => {
    if (!value) return minValue;
    return `${minValue} + ${value}`;
  };

  const getCreditsWithBestVoucher = (creditsValue) => {
    if (bestVoucher?.type === VOUCHER_TYPE.CHARACTER)
      return bestVoucher?.value * creditsValue;
    if (bestVoucher?.type === VOUCHER_TYPE.FIXED_CHARACTER)
      return Number(bestVoucher?.value) + Number(creditsValue);
    return creditsValue;
  };

  return (
    <StyledCreditSelectorWrapper>
      <NewCustomOptions
        headerOption={headerOption}
        defaultValue={selectedOption}
        value={selectedOption}
        onChange={(newValue) => onCreditChange(newValue.value)}
        infoOption={infoOption}
        detailInfoOption={renderTooltipOption()}
        options={options || []}
        note={
          pkg?.note && (
            <div
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(pkg?.note?.[language]),
              }}
            />
          )
        }
        inputType="number"
        dataKey="credits"
        handleClickShowPopup={() => handleClickShowPopup(tooltip?.[language])}
      />

      <DetailsBox>
        {(apiTtsSttLaunchEnabled
          ? pkg?.contents
          : pkg?.featuresDescription
        )?.map((item) => (
          <DetailsBox.Item key={item?.[language]}>
            <span
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(
                  t(item?.[language], {
                    credits: formatNumberWithSuffix(
                      getCreditsWithBestVoucher(optionsSelected?.credits),
                      language,
                    ),
                    devices: getLocalizedText(
                      getMinValue('devices'),
                      optionsSelected?.devices,
                    ),
                    voiceClonings: getLocalizedText(
                      getMinValue('voiceClonings'),
                      optionsSelected?.voiceClonings,
                    ),
                    concurrentRequestApi: getLocalizedText(
                      getMinValue('concurrentRequestApi'),
                      optionsSelected?.concurrentRequestApi,
                    ),
                  }),
                  {
                    allowedTags: ['span', 'strong', 'b', 'em', 'i'],
                    allowedAttributes: {
                      span: ['style'],
                    },
                  },
                ),
              }}
            />
          </DetailsBox.Item>
        ))}
      </DetailsBox>
    </StyledCreditSelectorWrapper>
  );
};

// === Sub-component: ResourceSelector ===
const ResourceSelector = ({
  resource,
  selectedValue,
  onValueChange,
  language,
  handleClickShowPopup,
}) => {
  const isVietNam = checkVietNam();
  const { t } = useTranslation();

  if (!resource) return null;

  const { options, title, description, defaultOption, key } = resource;

  const selectedOption =
    options?.find((opt) => opt.value === selectedValue) || options?.[0];

  const headerOption = (
    <StyledOptionLabel>{title?.[language]}</StyledOptionLabel>
  );

  const infoOption = description ? (
    <div
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{
        __html: sanitizeHtml(
          t(description[language], {
            pricePerUnit: formatPrice({
              vndPrice: defaultOption?.vndPricePerUnit,
              usdPrice: defaultOption?.usdPricePerUnit,
              language,
              isVietNam,
            }),
          }),
          {
            allowedTags: ['span'],
            allowedAttributes: { span: ['style'] },
          },
        ),
      }}
    />
  ) : null;

  return (
    <NewCustomOptions
      headerOption={headerOption}
      defaultValue={selectedOption}
      value={selectedOption}
      onChange={(newValue) => onValueChange(newValue.value)}
      infoOption={infoOption}
      options={options || []}
      inputType="number"
      dataKey={key}
      handleClickShowPopup={handleClickShowPopup}
      marginBottom="0px"
    />
  );
};

// ============================================================
// Main Component
// ============================================================
const NewCustomPackage = ({
  packagesComparisonEnabled,
  scrollToPackagesComparison,
  pkg,
  isMobile,
  hasDiscountPackage,
  duration,
  currentUsingPackage,
  vouchers,
  buyContainerMinHeight = 80,
}) => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const location = useLocation();
  const { voucherCode } = camelcaseKeys(
    queryString.parse(location.search) || {},
  );
  const dispatch = useDispatch();
  const { user = {} } = useSelector((state) => state.auth);

  const [optionsSelected, setOptionsSelected] = useState({
    credits: 0,
    devices: 0,
    voiceClonings: 0,
    concurrentRequestApi: 0,
  });
  const [openPopup, setOpenPopup] = useState(false);
  const [tooltipDetail, setTooltipDetail] = useState('');
  const [
    remainingValueOfTheCurrentPackage,
    setRemainingValueOfTheCurrentPackage,
  ] = useState({ price: 0, usdPrice: 0 });
  const [adjustedPackagePrice, setAdjustedPackagePrice] = useState(0);

  const inDiscountTime = checkPackageDiscount(pkg?.discountConfig);
  const isVietNam = checkVietNam();

  const { getFeatureValue } = useFeatureFlags();
  const { ip } = useIpLocation();
  const enableMergePackageApiToStudio = getFeatureValue(
    FEATURE_KEYS.MERGE_PACKAGE_API_TO_STUDIO,
    { ip },
  );
  const apiTtsSttLaunchEnabled = getFeatureValue(
    FEATURE_KEYS.LAUNCH_API_TTS_STT,
    {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
      ip,
    },
  );

  // === Compute custom resources ===
  const customResources = useMemo(() => {
    const { customResource = {} } = pkg || {};
    const sortedKeys = sortCustomResourceKeys(customResource);

    return sortedKeys
      .filter((key) => {
        if (key === RESOURCES_TYPE.CONCURRENT_REQUEST_API) {
          return enableMergePackageApiToStudio;
        }
        return true;
      })
      .map((key) => {
        const { prices, step } = customResource[key];
        const options = generateSelectOptions({ prices, step, resource: key });
        const defaultOption = options[0];
        return {
          ...customResource[key],
          options,
          defaultOption,
          key,
        };
      });
  }, [pkg, enableMergePackageApiToStudio]);

  // Separate credits from other resources
  const creditResource = customResources.find(
    (r) => r.key === RESOURCES_TYPE.CREDITS,
  );
  const otherResources = customResources.filter(
    (r) => r.key !== RESOURCES_TYPE.CREDITS,
  );

  // Compute default options synchronously so price is correct on first render
  const defaultOptions = useMemo(() => {
    if (!customResources?.length) return {};
    return customResources.reduce((acc, resource) => {
      const { key, defaultOption } = resource || {};
      return {
        ...acc,
        [key]: defaultOption?.value || 0,
      };
    }, {});
  }, [customResources]);

  // Use defaultOptions as fallback when Redux state hasn't been initialized yet
  // (credits=0 in Redux initial state means "not yet initialized" since the minimum selectable credit is always > 0)
  const activeOptions = useMemo(() => {
    const hasBeenInitialized =
      optionsSelected.credits > 0 || !defaultOptions.credits;
    if (hasBeenInitialized) return optionsSelected;
    return { ...optionsSelected, ...defaultOptions };
  }, [optionsSelected, defaultOptions]);

  // Still dispatch to Redux for other components that depend on optionsSelected
  useEffect(() => {
    if (customResources?.length) {
      const initializePrices = customResources.reduce((acc, resource) => {
        const { key, defaultOption } = resource || {};
        return {
          ...acc,
          [key]: defaultOption?.value || 0,
        };
      }, {});
      dispatch(actions.package.updateOptionsSelected(initializePrices));
      setOptionsSelected(initializePrices);
    }
  }, [customResources]);

  // === Price calculations ===
  const { originalPrice, price, vndPrice } = getCustomPackagePrice({
    credits: activeOptions.credits,
    voiceClonings: activeOptions.voiceClonings,
    devices: activeOptions.devices,
    ...(enableMergePackageApiToStudio && {
      concurrentRequestApi: activeOptions.concurrentRequestApi,
    }),
    customResource: pkg?.customResource,
    isVietNam,
  });

  const [bestVoucher] = getOptimalVouchersForPackage({
    pkg: {
      ...pkg,
      price,
      vndPrice,
      usdPrice: price,
      custom: { credits: activeOptions.credits },
    },
    vouchers,
  });

  const shouldShowDiscount =
    (bestVoucher?.value &&
      [VOUCHER_TYPE.PERCENT, VOUCHER_TYPE.PRICE].includes(bestVoucher?.type)) ||
    originalPrice !== price;

  const priceWithSaleOff = getPriceFromVoucher({
    isVietNam,
    voucher: bestVoucher,
    currPrice: price,
    currUsdPrice: price,
    roundedPrice: true,
  });

  // === Handlers ===
  const handleOptionChange = (key, value) => {
    setOptionsSelected((prev) => ({ ...prev, [key]: value }));
    dispatch(
      actions.package.updateOptionsSelected({
        ...optionsSelected,
        [key]: value,
      }),
    );
  };

  const handleClickShowPopup = (tooltip) => {
    setTooltipDetail(tooltip);
    if (isMobile) setOpenPopup(true);
  };

  // === CRM-specific: Remaining value logic ===
  const isCustomPackage = checkStudioCustomPackage(
    currentUsingPackage?.type,
    currentUsingPackage?.level,
  );

  const getAndSetRemainingValueOfTheCurrentPackage = async (
    packageType,
    device,
    voiceClonings,
  ) => {
    const data = await apis.orders.remainingValueOfTheCurrentPackage({
      packageType,
      device,
      maxCloningVoices: voiceClonings,
    });

    if (data?.status) setRemainingValueOfTheCurrentPackage(data?.result);
  };

  useEffect(() => {
    getAndSetRemainingValueOfTheCurrentPackage(
      location.pathname.includes('studio')
        ? PACKAGE_TYPE.STUDIO
        : PACKAGE_TYPE.API,
      activeOptions.devices,
      activeOptions.voiceClonings,
    );
  }, [location, activeOptions]);

  useEffect(() => {
    if (price && pkg && remainingValueOfTheCurrentPackage) {
      const basePrice = price;
      const baseUsdPrice = price;
      const { price: remainingPrice = 0, usdPrice: remainingUsdPrice = 0 } =
        remainingValueOfTheCurrentPackage || {};

      const adjustedPrice = Math.max(0, basePrice - remainingPrice);
      const adjustedUsdPrice = Math.max(0, baseUsdPrice - remainingUsdPrice);

      const savedPrice = getSavedPrice({
        voucher: bestVoucher,
        currPrice: adjustedPrice,
        currUsdPrice: adjustedUsdPrice,
      });

      const totalRemainingPrice = getTotalPrice({
        voucher: bestVoucher,
        currPrice: adjustedPrice,
        currUsdPrice: adjustedUsdPrice,
        savedPrice,
      });

      setAdjustedPackagePrice(totalRemainingPrice);
    }
  }, [remainingValueOfTheCurrentPackage, bestVoucher, pkg, price, duration]);

  // === CRM-specific: MoEngage tracking ===
  const sendEventAddToCartMoEngage = (paymentUrl) => {
    const [product, packageItem, period] = (pkg?.code || '').split('-');
    const hasRemainingValue =
      checkStudioCustomPackage(
        currentUsingPackage?.type,
        currentUsingPackage?.level,
      ) || isCustomPackage;
    const hasSaleOff = hasDiscountPackage || shouldShowDiscount;

    let finalPrice;
    if (hasRemainingValue) {
      finalPrice = adjustedPackagePrice;
    } else if (hasSaleOff) {
      finalPrice = priceWithSaleOff;
    } else {
      finalPrice = price;
    }

    const addToCartEventData = {
      event: EVENT_TYPE.ADD_TO_CART,
      userId: user?.id,
      email: user.email,
      package_code: pkg?.code,
      product,
      plan: packageItem,
      period,
      voucher_type: bestVoucher?.type,
      voucher_code: bestVoucher?.code,
      voucher_amount: bestVoucher?.value,
      voucher_title: isVietNam
        ? bestVoucher?.title?.vi
        : bestVoucher?.title?.en,
      package_price: pkg?.price,
      price: finalPrice,
      ...(hasRemainingValue
        ? { remaining_package_value: remainingValueOfTheCurrentPackage?.price }
        : {}),
      isTopUp: false,
      url: paymentUrl,
      currency_unit: isVietNam
        ? PAYMENT_CURRENCY_UNIT.VND
        : PAYMENT_CURRENCY_UNIT.USD,
      custom_credits: activeOptions.credits,
      custom_devices: activeOptions.devices,
      custom_max_cloning_voices: activeOptions.voiceClonings,
      ...(activeOptions.concurrentRequestApi && {
        custom_api_ccr: activeOptions.concurrentRequestApi,
      }),
    };
    window.dataLayer.push(addToCartEventData);
  };

  // === Render ===
  return (
    <StyledNewCustomPackageWrapper isMobile={isMobile}>
      <StyledNewCustomPackageCard>
        <StyledNewCustomPackageContent>
          {/* === TOP CONTENT === */}
          <div className="top-section pricing-top-section">
            {/* Header */}
            <div className="header-section">
              <NewPackageHeader
                data={pkg}
                inDiscountTime={inDiscountTime}
                bestVoucher={bestVoucher}
              />
            </div>

            {/* Price */}
            <div className="price-section">
              <div className="price-display">
                <NewPackagePrice
                  isVietnam={isVietNam}
                  pricing={{
                    price: priceWithSaleOff,
                    originalPrice,
                  }}
                  shouldShowDiscount={!!shouldShowDiscount}
                />
              </div>

              {/* Credit Selector */}
              <div>
                <CreditSelector
                  creditResource={creditResource}
                  selectedCredits={activeOptions.credits}
                  onCreditChange={(value) =>
                    handleOptionChange(RESOURCES_TYPE.CREDITS, value)
                  }
                  language={language}
                  handleClickShowPopup={handleClickShowPopup}
                  pkg={pkg}
                  bestVoucher={bestVoucher}
                  optionsSelected={activeOptions}
                  apiTtsSttLaunchEnabled={apiTtsSttLaunchEnabled}
                />
              </div>
            </div>

            {/* Buy Button */}
            <div
              className="buy-button-section"
              style={{ minHeight: buyContainerMinHeight }}
            >
              <BuyPackageButton
                packageCode={pkg?.code}
                voucherCode={bestVoucher?.code || voucherCode}
                credits={activeOptions?.credits}
                devices={activeOptions?.devices}
                voiceClonings={activeOptions?.voiceClonings}
                concurrentRequestApi={activeOptions?.concurrentRequestApi}
                onBeforeBuy={(url) => sendEventAddToCartMoEngage(url)}
              />
            </div>
          </div>

          {/* === DIVIDER === */}
          <div className="divider-section">
            <div className="divider-line" />
          </div>

          {/* === BOTTOM SECTION === */}
          <div className="bottom-section">
            {/* Section Title */}
            <span className="features-title">
              {pkg?.featuresTitle?.[language]}
            </span>

            {/* Features Description */}
            {apiTtsSttLaunchEnabled && (
              <FeaturesDescriptionList
                featuresDescription={pkg?.featuresDescription}
              />
            )}

            {/* Resource Selectors */}
            <div className="resources-section">
              {otherResources.map((resource) => (
                <ResourceSelector
                  key={resource.key}
                  resource={resource}
                  selectedValue={activeOptions[resource.key]}
                  onValueChange={(value) =>
                    handleOptionChange(resource.key, value)
                  }
                  language={language}
                  handleClickShowPopup={handleClickShowPopup}
                />
              ))}
              {packagesComparisonEnabled && (
                <CustomTypography
                  variant="body-14-semibold"
                  color="#1C1C1EA3"
                  onClick={scrollToPackagesComparison}
                  style={{
                    cursor: 'pointer',
                  }}
                >
                  {t('viewDetail')}{' '}
                  <ArrowForwardIosRoundedIcon
                    sx={{ fontSize: 12, color: '#1C1C1EA3' }}
                  />
                </CustomTypography>
              )}
            </div>
          </div>
        </StyledNewCustomPackageContent>

        {/* Mobile Popup for tooltip */}
        {openPopup && isMobile && (
          <NotificationDialogV3
            name={t('detailedInformation')}
            title={t('detailedInformation')}
            description={sanitizeHtml(tooltipDetail, {
              allowedTags: ['ul', 'li'],
              allowedAttributes: {
                li: ['style'],
                ul: ['style'],
              },
              transformTags: {
                li: (_, attribs) => ({
                  tagName: 'li',
                  attribs: {
                    ...attribs,
                    style: 'list-style-type: disc;',
                  },
                }),
              },
            })}
            open={openPopup && isMobile}
            onClose={() => setOpenPopup(false)}
            variant="null"
            fitWidth
            actionComponent={
              <Button
                onClick={() => setOpenPopup(false)}
                className="btn-close-popup"
                variant="contained"
              >
                {t('gotIt')}
              </Button>
            }
          />
        )}
      </StyledNewCustomPackageCard>
    </StyledNewCustomPackageWrapper>
  );
};

export default NewCustomPackage;
